import { useMemo } from 'react';
import { Col, Form, Row, List } from 'antd';
import { FormikProps } from 'formik';
import { MInput, MSelect } from '@Components/Base';
import { Option, OptionNum } from '@Components/types/options';
import ConditionalFormDetails from './component/ConditionalFormDetails';
import { AttendanceRequestFormValues } from '../types';

import styles from './request-details.module.css';

interface RequestDetailsSectionProps {
  formik: FormikProps<AttendanceRequestFormValues>;
  label: string;
  requestTypes: Option[];
  workLocationTypes: OptionNum[];
  companies: Option[];
  departmentProjects: OptionNum[];
  reasonsType: Option[];
  projectNDepartments: string[];
  findDeptProjName: (
    deptProjID: number | null,
    wlId: number | null
  ) => string | null;
  onReqTypeChange: (reqType: number) => void;
}

const RequestDetailsSection: React.FC<RequestDetailsSectionProps> = ({
  formik,
  requestTypes,
  projectNDepartments,
  workLocationTypes,
  companies,
  departmentProjects,
  findDeptProjName,
  reasonsType,
  onReqTypeChange,
}) => {
  const deptProjValueName = useMemo(
    () => findDeptProjName(formik.values.deptProjID, formik.values.wlId),
    [formik.values.deptProjID, formik.values.wlId, findDeptProjName]
  );

  const reasonValueName = useMemo(() => {
    return reasonsType.find(
      (item) => Number(item.value) === formik.values.groupID
    )?.label;
  }, [formik.values.groupID]);

  const requestTypeItem = useMemo(() => {
    const findedItem = requestTypes.find((item) => {
      return Number(item.value) === Number(formik.values.leaveTypeID);
    });
    return findedItem;
  }, [requestTypes, formik.values.leaveTypeID]);

  if (!requestTypes) return null;

  const {
    leaveTypeID: leaveTypeTouched,
    currentLocation: currentLocationTouched,
  } = formik.touched;
  const { leaveTypeID: leaveTypeError, currentLocation: currentLocationError } =
    formik.errors;

  return (
    <Row>
      <Col span={24}>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Request Type"
              validateStatus={leaveTypeTouched && leaveTypeError ? 'error' : ''}
              help={leaveTypeTouched && leaveTypeError ? leaveTypeError : ''}
            >
              <MSelect
                labeloption="Request Type"
                value={requestTypeItem?.label}
                options={requestTypes}
                onChange={(selectValue) => {
                  formik.setFieldValue('leaveTypeID', Number(selectValue));
                  onReqTypeChange(Number(selectValue));
                }}
                onBlur={() => {
                  formik.setFieldTouched('leaveTypeID', true);
                }}
                placeholder="Select Request Type"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Current Location"
              validateStatus={
                currentLocationTouched && currentLocationError ? 'error' : ''
              }
              help={
                currentLocationTouched && currentLocationError
                  ? currentLocationError
                  : ''
              }
            >
              <MInput
                value={formik.values?.currentLocation}
                onChange={(value) =>
                  formik.setFieldValue('currentLocation', value.target.value)
                }
                onBlur={() => {
                  formik.setFieldTouched('currentLocation', true);
                }}
                placeholder="Type Current Location"
              />
            </Form.Item>
          </Col>
          {Boolean(projectNDepartments.length) && (
            <Col span={24}>
              <Form.Item label="Project/Department">
                <List
                  className={styles.listProjects}
                  dataSource={projectNDepartments}
                  renderItem={(item) => <List.Item>{item}</List.Item>}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Col>
      <ConditionalFormDetails
        requestType={requestTypeItem?.label || ''}
        companies={companies}
        workLocationTypes={workLocationTypes}
        departmentProjects={departmentProjects}
        deptProjValueName={deptProjValueName}
        reasonValueName={reasonValueName}
        reasonsType={reasonsType}
        formik={formik}
      />
    </Row>
  );
};

export default RequestDetailsSection;
